// lib/island/globalSearch.ts
// Tippfehler-tolerante ⌘K-Suche via einem einzigen RPC-Aufruf (global_suche,
// SQL/pg_trgm, Migration global_suche_trgm). Löst den früheren Fan-out über
// ~13 Einzel-Queries ab.

import { useState, useEffect } from 'react';
import { supabase } from '../supabase/client';
import { fuzzyScore } from './fuzzy';
import { sichereHttpUrl } from '../url';

export interface SearchHit {
  id: string;
  kind: 'contact' | 'hit';
  type: string;
  label: string;
  sub?: string;
  href?: string;
  quelle?: string;
  externalUrl?: string;
}

// Trimmt + entfernt SQL-LIKE-Wildcardzeichen aus der Nutzereingabe, damit ein
// Nutzer-„%"/„_" nicht als eigener Wildcard in der serverseitigen
// `'%'||q||'%'`-ILIKE-Pattern-Konstruktion wirkt. Keine Filter-Injection mehr
// möglich (kein .or()-Filterstring mehr auf dem Client — q geht als
// gebundener RPC-Parameter an die SQL-Funktion global_suche).
function sanitizeTerm(s: string): string {
  return s
    .replace(/[%_\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface GlobalSucheRow {
  typ: string;
  id: string;
  label: string;
  sub: string | null;
  score: number;
  extra: Record<string, unknown> | null;
}

// Feste href-Baupläne je Typ (Muster wie im früheren Fan-out) — Typen ohne
// Eintrag bekommen keinen href (Verteiler nutzt pendingTerminId/pendingEmailId
// bzw. es gibt gar keine Detailroute).
const HREF_BY_TYP: Record<string, (id: string) => string> = {
  ticket: (id) => `/admin/tickets/${id}`,
  rechnung: (id) => `/admin/rechnungen/${id}`,
  vertrag: (id) => `/admin/vertraege/${id}`,
};

// Gruppen-Reihenfolge wie im früheren Fan-out (Kontakte zuerst, dann nach
// Typ) — das fuzzyScore-Nachranking sortiert NUR innerhalb dieser Gruppen.
const GROUP_ORDER = [
  'contact', 'objekt', 'einheit', 'ticket', 'rechnung',
  'vertrag', 'termin', 'zaehler', 'email', 'dokument',
];

function groupKey(hit: SearchHit): string {
  return hit.kind === 'contact' ? 'contact' : hit.type;
}

function mapRow(row: GlobalSucheRow): SearchHit {
  const extra = row.extra ?? {};
  const sub = row.sub ?? undefined;

  // Kontakte (kontakte_unified) — typ = quelle direkt, extra {quelle}
  if (typeof extra.quelle === 'string') {
    const quelle = extra.quelle;
    return {
      id: `${quelle}:${row.id}`,
      kind: 'contact',
      type: quelle,
      label: row.label,
      sub,
      href: `/admin/kontakte/${quelle}/${row.id}`,
      quelle,
    };
  }

  // Einheiten — href auf die Einheit im Objekt-Detail
  if (row.typ === 'einheit') {
    const objektId = typeof extra.objekt_id === 'string' ? extra.objekt_id : undefined;
    return {
      id: row.id,
      kind: 'hit',
      type: 'einheit',
      label: row.label,
      sub,
      // Ohne objekt_id in extra kein Sprungziel innerhalb des Objekts möglich —
      // auf die Objektliste ausweichen statt eines toten (href: undefined) Klicks.
      href: objektId ? `/admin/objekte/${objektId}/einheit/${row.id}` : '/admin/objekte',
    };
  }

  // Dokumente — externalUrl = Datei; ohne sichere URL → Ablage
  if (row.typ === 'dokument') {
    const url = sichereHttpUrl(extra.datei_url as string | undefined) ?? sichereHttpUrl(extra.onedrive_url as string | undefined) ?? undefined;
    return {
      id: row.id,
      kind: 'hit',
      type: 'dokument',
      label: row.label,
      sub,
      href: url ? undefined : '/admin/dokumente',
      externalUrl: url,
    };
  }

  return {
    id: row.id,
    kind: 'hit',
    type: row.typ,
    label: row.label,
    sub,
    href: HREF_BY_TYP[row.typ]?.(row.id),
  };
}

export function useImmoSearch(query: string): { hits: SearchHit[]; loading: boolean; fehler: boolean } {
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  // Abfrage gescheitert → die Pille sagt das, statt fälschlich „Keine Treffer“ zu zeigen
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    const q = sanitizeTerm(query);
    if (q.length < 2) {
      setHits([]);
      setLoading(false);
      setFehler(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);

      const { data, error } = await supabase.rpc('global_suche', { q });
      if (cancelled) return;
      if (error || !data) {
        console.warn('global_suche fehlgeschlagen:', error);
        setHits([]);
        setFehler(true);
        setLoading(false);
        return;
      }

      setFehler(false);
      const rows = data as GlobalSucheRow[];

      // Innerhalb der Typ-Gruppen (Gruppen-Reihenfolge bleibt erhalten) primär nach dem
      // Server-Score (Trigram-Ähnlichkeit, 0–1.5) sortieren — das ist das eigentliche
      // Tippfehler-tolerante Ranking. fuzzyScore dient NUR als Tiebreaker zwischen
      // gleich bewerteten Treffern (kein Subsequence-Match → null → 0, neutral statt
      // bestraft — sonst würden echte Trigram-Treffer ohne Subsequenz abgewertet).
      const gescort = rows.map(row => {
        const h = mapRow(row);
        return { h, score: row.score, fuzzy: fuzzyScore(q, h.label) ?? 0 };
      });

      const byGroup = new Map<string, { h: SearchHit; score: number; fuzzy: number }[]>();
      for (const entry of gescort) {
        const key = groupKey(entry.h);
        const bucket = byGroup.get(key);
        if (bucket) bucket.push(entry);
        else byGroup.set(key, [entry]);
      }

      const result: SearchHit[] = [];
      const seen = new Set<string>();
      for (const key of [...GROUP_ORDER, ...byGroup.keys()]) {
        if (seen.has(key)) continue;
        seen.add(key);
        const bucket = byGroup.get(key);
        if (!bucket) continue;
        bucket.sort((a, b) => b.score - a.score || b.fuzzy - a.fuzzy);
        for (const { h } of bucket) result.push(h);
      }

      setHits(result);
      setLoading(false);
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  return { hits, loading, fehler };
}
